import colors from '@colors/colors/safe.js';
import { createLogger, transports, format } from 'winston';
import { release, environment } from '../../sentry.config.mjs';
import { serverOnly$ } from 'vite-env-only/macros';

const {
  LOG_LEVEL = 'info',
  LOG_FORMAT = 'json',
  LOG_JSON_INDENT = '0',
  LOG_INCLUDE_SERVICE_INFO = 'true',
} = process.env;

const space = parseInt(LOG_JSON_INDENT, 10);

const colorsByLevel = {
  debug: colors.blue,
  error: colors.red,
  info: colors.green,
  log: colors.green,
  warn: colors.yellow,
};

function formatLevel(level: string) {
  const key = (level || 'log') as keyof typeof colorsByLevel;
  const fn = colorsByLevel[key] ?? ((input) => input);
  return fn(key.padEnd(5).substring(0, 5).toUpperCase());
}

/**
 * The serverOnly$ macro is used here to ensure standard console logging is used
 * in JS code that runs in the browser and winston is used in nodejs.
 * The standard remix approach to keeping this file out of client side code
 * (ie naming this file logger.server.ts) posed problems when building shared
 * data loaders and actions.
 * With this macro, developers can just always use the logger without having to
 * think about everywhere the code will run.
 * Note: Since jest has it's own way of building TS files that does not involve vite,
 * this must be mocked in unit tests (see logger.mock.ts).
 * @see https://remix.run/docs/en/main/discussion/server-vs-client#vite-env-only
 */
export const logger =
  serverOnly$(
    createLogger({
      defaultMeta: {
        service:
          LOG_INCLUDE_SERVICE_INFO === 'true'
            ? {
                name: 'flexcar-cfa',
                environment,
                version: release,
              }
            : undefined,
      },
      exitOnError: false,
      format: LOG_FORMAT.startsWith('json')
        ? format.combine(format.timestamp(), format.json({ space }))
        : format.combine(
            format.timestamp(),
            format.printf((entry) => {
              const { context, level, message, timestamp, ...rest } = entry;
              return `${timestamp} ${formatLevel(level)}${context ? ` [${context}] ` : ' '}${message}${Object.keys(rest).length ? ` ${JSON.stringify(rest, undefined, space)}` : ''}`;
            })
          ),
      level: LOG_LEVEL,
      transports: [new transports.Console()],
    })
  ) ?? console;

export default logger;
